import { ProTable } from '@ant-design/pro-components'
import { PatientWeightEditDrawer } from '@app/icare/entities/patient/edits/PatientWeightEditDrawer'
import { patientWeightQueries } from '@app/icare/entities/patient/queries/patientWeightQueries'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { sortByActiveRange } from '@app/icare/shared/api'
import { t } from '@lingui/core/macro'
import { entityParams } from '@pkg/db/utils'
import { formatDate } from '@pkg/shared/utils'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Flex } from 'antd'
import ReactECharts from 'echarts-for-react'
import { useMemo } from 'react'
import { FaPencil } from 'react-icons/fa6'

export const Route = createFileRoute('/_app/patienten/$id/_edit/anamnese/gewicht')({
  params: entityParams({ id: PatientId }),
  loader: ({ params, context: { queryClient } }) =>
    queryClient.ensureQueryData(patientWeightQueries.forList(params.id)),
  component: RouteComponent,
})

function RouteComponent() {
  const { id: patientId } = Route.useParams()
  const { data: weights = [] } = useSuspenseQuery(patientWeightQueries.forList(patientId))

  const chartOption = useMemo(() => {
    const sorted = [...weights].sort(sortByActiveRange)
    return {
      xAxis: {
        type: 'category',
        name: 'Datum',
        data: sorted.map(w => w.activeFrom),
        axisLabel: { formatter: (v: string) => formatDate(v) },
      },
      yAxis: { type: 'value', name: 'Gewicht (kg)' },
      series: [
        {
          name: 'Gewicht',
          type: 'line',
          data: sorted.map(w => w.weight),
          smooth: true,
          itemStyle: { color: '#1890ff' },
          lineStyle: { width: 2 },
          symbolSize: 8,
        },
      ],
    }
  }, [weights])

  return (
    <Flex vertical gap="small">
      {weights.length > 0 && <ReactECharts option={chartOption} style={{ height: '300px' }} />}
      <ProTable
        search={false}
        options={false}
        rowKey="id"
        dataSource={weights}
        size="small"
        locale={{ emptyText: t`Keine Gewichtsdaten vorhanden` }}
        columns={[
          {
            title: t`Gewicht (kg)`,
            dataIndex: 'weight',
            valueType: 'digit',
            fieldProps: { precision: 1, decimalSeparator: ',' },
          },
          {
            title: t`Datum`,
            dataIndex: 'activeFrom',
            defaultSortOrder: 'descend',
            sorter: sortByActiveRange,
          },
          {
            valueType: 'option',
            render: (_t, r) => [
              <Button
                key="edit"
                type="text"
                size="small"
                icon={<FaPencil />}
                onClick={() => PatientWeightEditDrawer.show({ patientId, relationId: r.id })}
              />,
            ],
          },
        ]}
      />
    </Flex>
  )
}
